import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Search, ClipboardList, Rocket, Zap, Microscope, Trash2, Play, Eye, BarChart3, RotateCcw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AnalyseHome = ({
  model, selectedMode, setSelectedMode, isRapid, setIsRapid,
  selectedCompany, setSelectedCompany, savedAnalyses,
  onStart, onResume, onDelete, onRelaunch, onViewResults, onViewHistory
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [relaunchTarget, setRelaunchTarget] = useState(null);
  const [relaunchName, setRelaunchName] = useState('');
  const searchTimeout = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) { setSearchResults([]); return; }
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await axios.get(`${API}/market/search`, { params: { q: searchQuery } });
        setSearchResults((data.results || []).filter(r => r.type === 'EQUITY').slice(0, 8));
      } catch { setSearchResults([]); }
      setSearching(false);
    }, 400);
    return () => clearTimeout(searchTimeout.current);
  }, [searchQuery]);

  useEffect(() => {
    const handler = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setSearchResults([]); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectCompany = (result) => {
    setSelectedCompany({ symbol: result.symbol, name: result.name });
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleStart = () => {
    if (!selectedCompany) { toast.error('Sélectionnez une entreprise'); return; }
    onStart(selectedCompany, selectedMode, isRapid);
  };

  const getVerdict = (analysis) => {
    if (!analysis.totalScore) return { icon: '?', color: 'text-cap-text-muted' };
    const pct = analysis.totalScore.percent;
    if (pct >= 75) return { icon: '\u{1F7E2}', color: 'text-green-400' };
    if (pct >= 50) return { icon: '\u{1F7E1}', color: 'text-yellow-400' };
    return { icon: '\u{1F534}', color: 'text-red-400' };
  };

  const rapidCount = model.meta.mode_rapide.sections.length;
  const totalSections = model.qualite.length;
  const inProgress = savedAnalyses.filter(a => a.status === 'in_progress');
  const completed = savedAnalyses.filter(a => a.status === 'completed');
  const tickerCounts = {};
  completed.forEach(a => { tickerCounts[a.ticker] = (tickerCounts[a.ticker] || 0) + 1; });

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto space-y-8" data-testid="analyse-home">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mb-2">Analyser</h1>
        <p className="text-cap-text-secondary">Analyse fondamentale guidée d'entreprises en {totalSections} sections</p>
      </div>

      {/* Search */}
      <div className="relative" ref={dropdownRef}>
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-cap-text-muted" />
          <Input
            data-testid="analyse-search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher une entreprise a analyser..."
            className="pl-12 h-14 text-lg bg-cap-bg-elevated border-white/10 text-white placeholder:text-cap-text-muted"
          />
          {searching && (
            <div className="absolute right-4 top-1/2 -translate-y-1/2">
              <div className="w-5 h-5 border-2 border-cap-emerald border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>
        {searchResults.length > 0 && (
          <div className="absolute z-50 w-full mt-2 bg-cap-bg-elevated border border-white/10 rounded-xl shadow-xl overflow-hidden">
            {searchResults.map((r, i) => (
              <button
                key={i}
                onClick={() => selectCompany(r)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 transition-colors text-left"
                data-testid={`search-result-${i}`}
              >
                <div>
                  <span className="text-white font-medium">{r.name}</span>
                  <span className="text-cap-text-muted ml-2 text-sm">{r.symbol}</span>
                </div>
                <span className="text-cap-text-muted text-xs">{r.exchange}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Selected Company */}
      {selectedCompany && (
        <div className="flex items-center gap-3 bg-cap-emerald/10 border border-cap-emerald/20 rounded-xl px-4 py-3" data-testid="selected-company-badge">
          <div className="w-10 h-10 rounded-lg bg-cap-emerald/20 flex items-center justify-center">
            <span className="font-display font-bold text-cap-emerald text-sm">{selectedCompany.symbol.slice(0, 2)}</span>
          </div>
          <div>
            <p className="text-white font-medium">{selectedCompany.name}</p>
            <p className="text-cap-text-muted text-sm">{selectedCompany.symbol}</p>
          </div>
          <button onClick={() => setSelectedCompany(null)} className="ml-auto text-cap-text-muted hover:text-white">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Mode Selection */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <button
          data-testid="mode-qualite-btn"
          onClick={() => setSelectedMode('qualite')}
          className={`p-5 rounded-xl border transition-all text-left ${
            selectedMode === 'qualite'
              ? 'bg-cap-emerald/10 border-cap-emerald/40 shadow-glow-emerald'
              : 'bg-cap-bg-elevated border-white/5 hover:border-white/20'
          }`}
        >
          <div className="flex items-center gap-3 mb-2">
            <ClipboardList className={`w-6 h-6 ${selectedMode === 'qualite' ? 'text-cap-emerald' : 'text-cap-text-secondary'}`} />
            <span className={`font-display font-bold text-lg ${selectedMode === 'qualite' ? 'text-cap-emerald' : 'text-white'}`}>
              Qualité Long Terme
            </span>
          </div>
          <p className="text-cap-text-muted text-sm">Identifier les entreprises solides pour un investissement durable</p>
        </button>
        <button
          data-testid="mode-potentiel-btn"
          onClick={() => setSelectedMode('potentiel')}
          className={`p-5 rounded-xl border transition-all text-left ${
            selectedMode === 'potentiel'
              ? 'bg-cap-orange/10 border-cap-orange/40'
              : 'bg-cap-bg-elevated border-white/5 hover:border-white/20'
          }`}
        >
          <div className="flex items-center gap-3 mb-2">
            <Rocket className={`w-6 h-6 ${selectedMode === 'potentiel' ? 'text-cap-orange' : 'text-cap-text-secondary'}`} />
            <span className={`font-display font-bold text-lg ${selectedMode === 'potentiel' ? 'text-cap-orange' : 'text-white'}`}>
              Fort Potentiel
            </span>
          </div>
          <p className="text-cap-text-muted text-sm">Détecter les entreprises à fort potentiel de croissance</p>
        </button>
      </div>

      {/* Toggle + Start */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <button
          data-testid="toggle-rapid-btn"
          onClick={() => setIsRapid(!isRapid)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
            isRapid ? 'bg-cap-blue/10 border-cap-blue/40 text-cap-blue' : 'bg-cap-bg-elevated border-white/10 text-cap-text-secondary'
          }`}
        >
          {isRapid ? <Zap className="w-4 h-4" /> : <Microscope className="w-4 h-4" />}
          <span className="font-medium text-sm">
            {isRapid ? `Mode Rapide (${rapidCount} sections)` : `Mode Complet (${totalSections} sections)`}
          </span>
        </button>
        <Button
          data-testid="start-analysis-btn"
          onClick={handleStart}
          disabled={!selectedCompany}
          className="bg-cap-emerald hover:bg-cap-emerald-hover text-black font-bold px-8 h-12"
        >
          <Play className="w-5 h-5 mr-2" /> Lancer l'analyse
        </Button>
      </div>

      {/* In-Progress */}
      {inProgress.length > 0 && (
        <div className="space-y-3">
          <h2 className="font-display text-lg font-bold text-white">Analyses en cours</h2>
          {inProgress.map(a => (
            <div key={a.id} className="flex items-center justify-between bg-cap-bg-elevated border border-white/5 rounded-xl p-4" data-testid={`inprogress-${a.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-cap-blue/20 flex items-center justify-center">
                  <span className="font-bold text-cap-blue text-xs">{a.ticker.slice(0, 2)}</span>
                </div>
                <div>
                  <p className="text-white font-medium">{a.companyName} <span className="text-cap-text-muted text-sm">({a.ticker})</span></p>
                  {a.versionName && <p className="text-cap-text-muted text-[11px]">{a.versionName}</p>}
                  <p className="text-cap-text-muted text-xs">{a.mode === 'qualite' ? 'Qualité LT' : 'Fort Potentiel'} | {a.date}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => onResume(a)} className="border-cap-blue/30 text-cap-blue hover:bg-cap-blue/10">
                  <Play className="w-4 h-4 mr-1" /> Reprendre
                </Button>
                <Button size="sm" variant="ghost" onClick={() => onDelete(a.id)} className="text-cap-text-muted hover:text-cap-error">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Completed */}
      {completed.length > 0 && (
        <div className="space-y-3">
          <h2 className="font-display text-lg font-bold text-white">Analyses terminées</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-left" data-testid="saved-analyses-table">
              <thead>
                <tr className="border-b border-white/5 text-cap-text-muted text-xs uppercase">
                  <th className="py-3 px-3">Entreprise</th>
                  <th className="py-3 px-3">Mode</th>
                  <th className="py-3 px-3">Score</th>
                  <th className="py-3 px-3">Date</th>
                  <th className="py-3 px-3">Verdict</th>
                  <th className="py-3 px-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {completed.map(a => {
                  const verdict = getVerdict(a);
                  return (
                    <tr key={a.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                      <td className="py-3 px-3">
                        <span className="text-white font-medium">{a.companyName}</span>
                        <span className="text-cap-text-muted text-sm ml-1">({a.ticker})</span>
                        {a.versionName && <p className="text-cap-text-muted text-[11px] mt-0.5">{a.versionName}</p>}
                      </td>
                      <td className="py-3 px-3 text-cap-text-secondary text-sm">{a.mode === 'qualite' ? 'Qualité LT' : 'Fort Potentiel'}</td>
                      <td className="py-3 px-3 text-white font-mono font-bold">{a.totalScore ? `${a.totalScore.percent.toFixed(0)}%` : '—'}</td>
                      <td className="py-3 px-3 text-cap-text-muted text-sm">{a.date}</td>
                      <td className="py-3 px-3 text-lg">{verdict.icon}</td>
                      <td className="py-3 px-3 text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => onViewResults(a)} className="text-cap-text-secondary hover:text-white" data-testid={`view-result-${a.id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => { setRelaunchTarget(a); setRelaunchName(''); }} className="text-cap-text-secondary hover:text-cap-emerald" data-testid={`relaunch-${a.id}`}>
                            <RotateCcw className="w-4 h-4" />
                          </Button>
                          {tickerCounts[a.ticker] > 1 && (
                            <Button size="sm" variant="ghost" onClick={() => onViewHistory(a.ticker)} className="text-cap-text-secondary hover:text-cap-blue" data-testid={`view-history-${a.ticker}`}>
                              <BarChart3 className="w-4 h-4" />
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => onDelete(a.id)} className="text-cap-text-muted hover:text-cap-error">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {savedAnalyses.length === 0 && (
        <div className="text-center py-16 bg-cap-bg-elevated/50 rounded-2xl border border-white/5" data-testid="empty-state">
          <Microscope className="w-12 h-12 text-cap-text-muted mx-auto mb-4" />
          <p className="text-cap-text-secondary text-lg mb-1">Aucune analyse sauvegardée</p>
          <p className="text-cap-text-muted text-sm">Recherchez une entreprise ci-dessus pour commencer</p>
        </div>
      )}
      {/* Relaunch Dialog */}
      <Dialog open={!!relaunchTarget} onOpenChange={() => setRelaunchTarget(null)}>
        <DialogContent className="bg-cap-bg-elevated border-white/10 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-cap-emerald" /> Relancer l'analyse
            </DialogTitle>
          </DialogHeader>
          {relaunchTarget && (
            <div className="space-y-4">
              <p className="text-cap-text-secondary text-sm">
                Nouvelle analyse de <strong className="text-white">{relaunchTarget.companyName}</strong> en mode {relaunchTarget.mode === 'qualite' ? 'Qualité LT' : 'Fort Potentiel'}.
                Les scores précédents seront pré-remplis.
              </p>
              <div>
                <label className="text-cap-text-muted text-xs block mb-1">Nom de cette version (optionnel)</label>
                <Input
                  data-testid="relaunch-name-input"
                  value={relaunchName}
                  onChange={(e) => setRelaunchName(e.target.value)}
                  placeholder="Ex : Après Q4 2025"
                  className="bg-cap-bg border-white/10 text-white placeholder:text-cap-text-muted"
                />
              </div>
              <Button
                data-testid="relaunch-confirm-btn"
                onClick={() => { onRelaunch(relaunchTarget, relaunchName.trim()); setRelaunchTarget(null); }}
                className="w-full bg-cap-emerald hover:bg-cap-emerald-hover text-black font-bold"
              >
                Continuer
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AnalyseHome;
